'use client';
import { Work } from '@mui/icons-material';
import { Box, CardMedia, CardProps } from '@mui/material';
import dynamic from 'next/dynamic';
import React, { useCallback, useMemo, useRef } from 'react';
import { useBreakpoint, useCustomNavigate, useSearch } from '@dh/hooks';
import { Company, isExpanded } from '@dh/types';
import { keyboardSelect } from '@dh/utils';
import { FormattedOutput } from '@dh/components';

import {
    CardContainer,
    CompanyCardContent,
    CompanyDescription,
    CompanyTitle,
    PlaceholderImage
} from './styles';

import { ChipContainerProps } from '../tag-container/TagContainer';

/* To avoid server from trying to access DOM element in component */
const TagContainer = dynamic<ChipContainerProps>(
    () => import('../tag-container/TagContainer').then((module) => module.TagContainer),
    { ssr: false }
);

interface CompanyCardProps extends Omit<CardProps, 'onClick'> {
    company: Company;
    onClick?: (Company: Company) => void;
    backgroundColor?: string;
    tagRows?: number;
    withDescription?: boolean;
}

export const CompanyCard = ({
    company,
    onClick,
    backgroundColor,
    tagRows,
    withDescription,
    ...cardProps
}: CompanyCardProps) => {
    const { navigateToCompanyDetails } = useCustomNavigate();
    const cardRef = useRef<HTMLDivElement | null>(null);
    const breakpoint = useBreakpoint();
    const goToDetails = useCallback(
        () => navigateToCompanyDetails(company.documentId),
        [company.documentId, navigateToCompanyDetails]
    );

    const { onSearch } = useSearch();

    const chipsContent = useMemo(
        () => company.topicsKnowledge?.map((topic) => topic.topic.title) ?? [],
        [company.topicsKnowledge]
    );

    const infoText = company.info?.text;
    const titlePicture = company.media?.[0];

    const tags = useMemo(
        () =>
            chipsContent.map((title) => ({
                title,
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    e.stopPropagation();
                    onSearch(title);
                }
            })),
        [chipsContent, onSearch]
    );

    const handleKeyboardGoToDetails = useCallback(
        (event: React.KeyboardEvent) => keyboardSelect(event, goToDetails),
        [goToDetails]
    );

    return (
        <CardContainer
            tabIndex={0}
            onKeyDown={handleKeyboardGoToDetails}
            ref={cardRef}
            breakpoint={breakpoint}
            onClick={goToDetails}
            style={{ backgroundColor: backgroundColor }}
            {...cardProps}
        >
            <Box height={223}>
                {titlePicture ? (
                    <CardMedia component={'img'} image={titlePicture.url} sx={{ height: 223 }} />
                ) : (
                    <PlaceholderImage>
                        <Work />
                    </PlaceholderImage>
                )}
            </Box>
            <CompanyCardContent>
                <CompanyTitle>{company.name}</CompanyTitle>
                {withDescription && infoText && (
                    <CompanyDescription>
                        <FormattedOutput>{company.info?.text}</FormattedOutput>
                    </CompanyDescription>
                )}
                <TagContainer tags={tags} rows={isExpanded(breakpoint) ? tagRows : 1} />
            </CompanyCardContent>
        </CardContainer>
    );
};
